import { useCallback, useEffect, useMemo, useState } from 'react';
import * as bd from 'react-basic-design';
import {
    RepresentativeDTO,
    useDeleteRepresentativeApi,
    useSaveRepresentativeApi,
    useSearchRepresentativeApi,
} from '../../../api/representative-api';
import SvgAdd from '../../../assets/icons/Add';
import { InitFailed } from '../../../components/basic/init-failed';
import { Loading } from '../../../components/basic/loading';
import { N } from '../../../components/basic/n';
import { notify } from '../../../components/basic/notify';
import { T, translate } from '../../../components/basic/text';
import { LoadingButton } from '../../../components/loading-button';
import { PageNavbar } from '../../../components/ui/page-navbar';
import { TProvider } from '../../../i18n';
import { IBasicTableColumn } from '../../../interfaces/interfaces';
import { SaveRepresentativeModal } from './save-representative-modal';

export function RepresentativeApp() {
    const [showModal, setShowModal] = useState(false);
    const [selectedRepresentative, setSelectedRepresentative] = useState<
        RepresentativeDTO | undefined
    >();

    const representativeInit = useSearchRepresentativeApi();
    const saveRepresentative = useSaveRepresentativeApi();
    const deleteRepresentative = useDeleteRepresentativeApi();

    const { result, error, busy, initialized, initializing } = representativeInit;

    const countryTitle = useCallback(
        (id?: string) => {
            var country = result?.countries.find(x => x.id === id);
            return <span>{country ? `${country.name} ` : id}</span>;
        },
        [result?.countries]
    );

    const columns: IBasicTableColumn<RepresentativeDTO>[] = useMemo(
        () => [
            {
                Header: <T>id</T>,
                name: 'id',
                Cell: ({ row }) => <N>{row.id}</N>,
            },
            {
                Header: <T>title</T>,
                name: 'title',
            },
            {
                Header: <T>country-id</T>,
                name: 'countryId',
                Cell: ({ row }) => countryTitle(row.countryId),
            },
            {
                Header: <T>region</T>,
                name: 'regionId',
                Cell: ({ row }) => (
                    <span>{regions.find(x => x.id === row.regionId)?.name || row.regionId}</span>
                ),
            },
            {
                Header: <T>contact</T>,
                name: 'contact',
            },
            {
                Header: <T>email</T>,
                name: 'email',
            },
            {
                Header: <T>business-hours-weekdays</T>,
                name: 'businessHoursWeekdays',
            },
            {
                Header: <T>business-hours-weekend</T>,
                name: 'businessHoursWeekend',
            },
        ],
        [countryTitle]
    );

    useEffect(() => {
        if (initialized || initializing || error) return;
        representativeInit.post(null, {});
    }, [initialized, initializing, error, representativeInit]);

    const handleDelete = (representative: RepresentativeDTO) => {
        bd.msgbox(
            translate('delete-representative'),
            translate('confirm-delete-representative'),
            [
                {
                    title: translate('delete'),
                    variant: 'contained',
                    color: 'secondary',
                    action: hide => {
                        deleteRepresentative.remove(
                            representative,
                            () => {
                                representativeInit.updateResult({
                                    ...result,
                                    representatives: result.representatives.filter(
                                        b => b.id !== representative.id
                                    ),
                                });
                                notify.dark(
                                    translate('selected-representative-deleted-successfully')
                                );
                                hide();
                                setShowModal(false);
                                setSelectedRepresentative(undefined);
                            },
                            notify.error
                        );
                    },
                },
                {
                    title: translate('cancel'),
                    variant: 'outline',
                    color: 'secondary',
                },
            ],
            { className: 'modal-n', backdropClassName: 'modal-n' }
        );
    };

    const handleSave = (representative: RepresentativeDTO) => {
        saveRepresentative.post(
            null,
            representative,
            res => {
                if (selectedRepresentative) {
                    representativeInit.updateResult({
                        ...result,
                        representatives: result.representatives.map(b =>
                            b.id === representative.id ? res.representative : b
                        ),
                    });
                } else {
                    representativeInit.updateResult({
                        ...result,
                        representatives: [res.representative, ...result.representatives],
                    });
                }

                setShowModal(false);
                setSelectedRepresentative(undefined);
                notify.success(
                    selectedRepresentative
                        ? translate('selected-representative-updated-successfully')
                        : translate('new-representative-created-successfully')
                );
            },
            notify.error
        );
    };

    if (error) return <InitFailed ex={error} onRetry={() => representativeInit.post(null, {})} />;
    if (!initialized || initializing) return <Loading />;
    const regions = result.regions;

    return (
        <TProvider>
            <div className="container">
                <PageNavbar
                    onGoBack="/home"
                    title="representatives"
                    controls={
                        <LoadingButton
                            variant="contained"
                            size="sm"
                            label={<T>new-representative</T>}
                            icon={<SvgAdd />}
                            onClick={() => {
                                setShowModal(true);
                                setSelectedRepresentative(undefined);
                            }}
                            loading={busy}
                        />
                    }
                />
                <bd.Paper className="h-100">
                    <bd.BasicTable
                        columns={columns}
                        data={result.representatives}
                        onRowClick={({ row }) => {
                            setSelectedRepresentative(row);
                            setShowModal(true);
                        }}
                        hover
                    >
                        <T as="div" className="nothing-found">
                            nothing-found
                        </T>
                    </bd.BasicTable>

                    <SaveRepresentativeModal
                        show={showModal}
                        onHide={() => {
                            setShowModal(false);
                            setSelectedRepresentative(undefined);
                        }}
                        regions={regions}
                        onSave={handleSave}
                        onDelete={handleDelete}
                        selectedRepresentative={selectedRepresentative}
                        countries={result?.countries}
                        allowEdit={result.allowEdit}
                    />
                </bd.Paper>
            </div>
        </TProvider>
    );
}
